// index.ts
import { z } from "zod";
var imageProviderIdSchema = z.enum(["doubao", "openai", "custom"]);
var imageProviderInputSchema = z.object({
  provider: imageProviderIdSchema,
  label: z.string().trim().min(2).max(80),
  baseUrl: z.url().max(500).refine((value) => {
    const url = new URL(value);
    const privateHost = /^(?:localhost|127\.|10\.|192\.168\.|169\.254\.|172\.(?:1[6-9]|2\d|3[01])\.|\[?::1\]?)$/i.test(url.hostname);
    return url.protocol === "https:" && !url.username && !url.password && !privateHost;
  }, "Image API URL must use public HTTPS without embedded credentials"),
  model: z.string().trim().min(1).max(160),
  apiKey: z.string().trim().max(4096).optional(),
  enabled: z.boolean().default(true)
});
var imageProviderSchema = imageProviderInputSchema.omit({ apiKey: true }).extend({
  hasApiKey: z.boolean(),
  status: z.enum(["not_configured", "not_tested", "connected", "error"]),
  lastTestedAt: z.iso.datetime().nullable(),
  lastError: z.string().nullable()
});
var imageProviderTestResultSchema = z.object({
  ok: z.boolean(),
  statusCode: z.number().int().nullable(),
  latencyMs: z.number().int().nonnegative(),
  testedAt: z.iso.datetime(),
  message: z.string()
});
var imageGenerationRequestSchema = z.object({
  provider: imageProviderIdSchema,
  prompt: z.string().trim().min(8).max(3e3),
  count: z.number().int().min(1).max(4).default(1),
  aspectRatio: z.enum(["square", "portrait", "landscape"]).default("square"),
  quality: z.enum(["low", "medium", "high"]).optional(),
  referenceImages: z.array(z.string().startsWith("data:image/").max(12e6)).max(4).optional(),
  strictReferenceValidation: z.boolean().optional()
});
var generatedImageSchema = z.object({
  id: z.string(),
  provider: imageProviderIdSchema,
  model: z.string(),
  url: z.string().min(1),
  revisedPrompt: z.string().nullable(),
  validationStatus: z.enum(["not_requested", "passed", "rejected", "error"]).optional(),
  validationMessage: z.string().nullable().optional(),
  retryInstruction: z.string().nullable().optional(),
  humanDecision: z.enum(["pending", "reasonable", "unreasonable"]).optional(),
  humanDecisionAt: z.iso.datetime().nullable().optional()
});
var generatedImageArchiveRecordSchema = generatedImageSchema.extend({
  createdAt: z.iso.datetime(),
  prompt: z.string(),
  humanDecision: z.enum(["pending", "reasonable", "unreasonable"]).default("pending"),
  humanDecisionAt: z.iso.datetime().nullable().default(null)
});
var generatedImageHumanDecisionInputSchema = z.object({
  decision: z.enum(["pending", "reasonable", "unreasonable"])
});
var imageGenerationResultSchema = z.object({
  provider: imageProviderIdSchema,
  model: z.string(),
  createdAt: z.iso.datetime(),
  images: z.array(generatedImageSchema).min(1).max(4)
});
var authorizedShopReviewAnalysisSchema = z.object({
  source: z.literal("pinduoduo-authorized-shop"),
  shopId: z.string().min(1),
  shopName: z.string().min(1),
  mallId: z.string().regex(/^\d+$/),
  analyzedAt: z.iso.datetime(),
  sourceCount: z.number().int().nonnegative(),
  capturedRows: z.number().int().nonnegative(),
  pages: z.number().int().positive(),
  platformCapped: z.boolean(),
  themes: z.array(z.object({ name: z.string(), mentions: z.number().int().positive() })).max(8),
  message: z.string()
});
var publicProductReviewAnalysisInputSchema = z.object({
  url: z.string().trim().url().max(1e3)
});
var publicProductReviewAnalysisSchema = z.object({
  source: z.literal("pinduoduo-public-product"),
  goodsId: z.string().regex(/^\d+$/),
  productUrl: z.url(),
  analyzedAt: z.iso.datetime(),
  visibleCount: z.number().int().nonnegative().nullable(),
  sourceCount: z.number().int().nonnegative(),
  capturedRows: z.number().int().nonnegative(),
  pages: z.number().int().positive(),
  platformCapped: z.boolean(),
  themes: z.array(z.object({ name: z.string(), mentions: z.number().int().positive() })).max(8),
  message: z.string()
});
var reviewTextGenerationRequestSchema = z.object({
  product: z.string().trim().min(1).max(200),
  audience: z.string().trim().max(120).default(""),
  benefits: z.string().trim().max(2e3).default(""),
  count: z.number().int().min(1).max(20),
  previousReviews: z.array(z.string().trim().min(1).max(1e3)).max(200).default([]),
  requirements: z.string().trim().max(1e3).default(""),
  brandName: z.string().trim().max(120).default(""),
  competitorThemes: z.array(z.string().trim().min(1).max(80)).max(8).default([])
});
var reviewTextGenerationResultSchema = z.object({
  model: z.string().min(1),
  texts: z.array(z.string().trim().min(1).max(1e3)).min(1).max(20)
});
var reviewLibraryInputSchema = z.object({
  sourceDraftId: z.string().trim().min(1),
  productName: z.string().trim().min(1),
  reviewText: z.string().trim().min(1),
  directions: z.array(z.string().trim().min(1)).min(1).max(8),
  images: z.array(generatedImageSchema).min(1).max(8),
  competitorUrl: z.string().nullable().default(null)
});
var reviewLibraryItemSchema = reviewLibraryInputSchema.extend({
  id: z.string(),
  savedAt: z.iso.datetime(),
  dingTalkSentAt: z.iso.datetime().nullable().default(null),
  dingTalkSendCount: z.number().int().nonnegative().default(0)
});
var mainImageLibraryInputSchema = z.object({
  sourceBatchId: z.string().trim().min(1),
  requirements: z.string().trim().min(1),
  images: z.array(generatedImageSchema).min(1).max(4)
});
var mainImageLibraryItemSchema = mainImageLibraryInputSchema.extend({
  id: z.string(),
  savedAt: z.iso.datetime()
});
export {
  authorizedShopReviewAnalysisSchema,
  generatedImageArchiveRecordSchema,
  generatedImageHumanDecisionInputSchema,
  generatedImageSchema,
  imageGenerationRequestSchema,
  imageGenerationResultSchema,
  imageProviderIdSchema,
  imageProviderInputSchema,
  imageProviderSchema,
  imageProviderTestResultSchema,
  mainImageLibraryInputSchema,
  mainImageLibraryItemSchema,
  publicProductReviewAnalysisInputSchema,
  publicProductReviewAnalysisSchema,
  reviewLibraryInputSchema,
  reviewLibraryItemSchema,
  reviewTextGenerationRequestSchema,
  reviewTextGenerationResultSchema
};
